import type { GraphEdge, GraphNode, GraphResult } from "@mind/contracts/browser";

export const GRAPH_NODE_WIDTH = 220;
export const GRAPH_NODE_HEIGHT = 72;
export const GRAPH_CANVAS_WIDTH = 980;
export const GRAPH_CANVAS_MIN_HEIGHT = 420;

export type GraphLayoutNode = {
  readonly node: GraphNode;
  readonly x: number;
  readonly y: number;
  readonly root: boolean;
};

export type GraphLayoutEdge = {
  readonly edge: GraphEdge;
  readonly source: GraphLayoutNode;
  readonly target: GraphLayoutNode;
  readonly path: string;
};

export type GraphLayout = {
  readonly width: number;
  readonly height: number;
  readonly nodes: readonly GraphLayoutNode[];
  readonly edges: readonly GraphLayoutEdge[];
};

export function graphNodeKey(node: Pick<GraphNode, "type" | "id">): string {
  return `${node.type}:${node.id}`;
}

export function graphEndpointKey(endpoint: GraphEdge["source"]): string {
  return `${endpoint.type}:${endpoint.id}`;
}

export function layoutGraph(graph: GraphResult, rootKey: string): GraphLayout {
  const sortedNodes = [...graph.nodes].sort((left, right) => {
    const leftIsRoot = graphNodeKey(left) === rootKey;
    const rightIsRoot = graphNodeKey(right) === rootKey;
    if (leftIsRoot !== rightIsRoot) return leftIsRoot ? -1 : 1;
    return graphNodeKey(left).localeCompare(graphNodeKey(right));
  });
  const root = sortedNodes.find((node) => graphNodeKey(node) === rootKey) ?? sortedNodes[0];
  if (root === undefined) return { width: GRAPH_CANVAS_WIDTH, height: GRAPH_CANVAS_MIN_HEIGHT, nodes: [], edges: [] };
  const neighbors = sortedNodes.filter((node) => node !== root);
  const height = Math.max(GRAPH_CANVAS_MIN_HEIGHT, 132 + neighbors.length * 96);
  const rootPosition: GraphLayoutNode = {
    node: root,
    x: 52,
    y: (height - GRAPH_NODE_HEIGHT) / 2,
    root: true,
  };
  const positioned = [
    rootPosition,
    ...neighbors.map((node, index) => ({
      node,
      x: 680,
      y: 54 + index * 96,
      root: false,
    })),
  ];
  const positions = new Map(positioned.map((item) => [graphNodeKey(item.node), item]));
  const edges = [...graph.edges]
    .sort((left, right) => {
      const typeOrder = left.linkType.localeCompare(right.linkType);
      if (typeOrder !== 0) return typeOrder;
      const sourceOrder = graphEndpointKey(left.source).localeCompare(graphEndpointKey(right.source));
      if (sourceOrder !== 0) return sourceOrder;
      return graphEndpointKey(left.target).localeCompare(graphEndpointKey(right.target));
    })
    .flatMap((edge) => {
      const source = positions.get(graphEndpointKey(edge.source));
      const target = positions.get(graphEndpointKey(edge.target));
      if (source === undefined || target === undefined) return [];
      return [{ edge, source, target, path: edgePath(source, target) }];
    });
  return { width: GRAPH_CANVAS_WIDTH, height, nodes: positioned, edges };
}

function edgePath(source: GraphLayoutNode, target: GraphLayoutNode): string {
  const sourceX = source.x + GRAPH_NODE_WIDTH / 2;
  const sourceY = source.y + GRAPH_NODE_HEIGHT / 2;
  const targetX = target.x + GRAPH_NODE_WIDTH / 2;
  const targetY = target.y + GRAPH_NODE_HEIGHT / 2;
  const controlX = (sourceX + targetX) / 2;
  return `M ${sourceX} ${sourceY} C ${controlX} ${sourceY}, ${controlX} ${targetY}, ${targetX} ${targetY}`;
}
